import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../lib/branding";
import { toast } from "sonner";

const MX_GRADIENT = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Form */}
      <div className="flex-1 bg-[#F0F1F7] flex flex-col justify-center px-10 py-12 lg:px-16 max-w-[560px]">
        {/* Logo */}
        <div className="mb-10">
          {branding.logo_data ? (
            <img src={branding.logo_data} alt={branding.name} className="h-12 w-auto max-w-[200px] object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white font-[900] text-lg flex-shrink-0"
                style={MX_GRADIENT}
              >
                M
              </div>
              <div>
                <div className="text-[15px] font-[800] tracking-tight text-[#1A1A2E] uppercase leading-none">
                  {branding.name}
                </div>
                <div className="text-[9px] font-[600] text-[#9CA3AF] tracking-[0.18em] uppercase mt-1">
                  Learning Hub
                </div>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-[38px] font-[900] text-[#1A1A2E] leading-tight mb-2">Welcome back.</h1>
        <p className="text-sm text-[#6B7280] mb-8">
          Sign in to continue on <span className="font-[600] text-[#1A1A2E]">{branding.name}</span>.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@mxacademy.in"
              data-testid="login-email"
              className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm text-[#1A1A2E] placeholder-[#C4C9DA]"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] font-[700] text-[#6B7280] uppercase tracking-[0.12em]">
                Password
              </label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-[11px] font-[600] text-[#6C47FF] hover:underline"
              >
                Forgot password? Contact your academy admin.
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
            style={MX_GRADIENT}
          >
            {loading ? "Signing in…" : "Sign in →"}
          </button>
        </form>
      </div>

      {/* Right Panel — Hero */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-center px-16 py-12 text-white relative overflow-hidden"
        style={MX_GRADIENT}
      >
        {/* Decorative circles */}
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[240px] h-[240px] rounded-full bg-white/10 pointer-events-none" />

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 text-xs font-[700] uppercase tracking-wider mb-8 w-fit">
            <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
            Live Cohort · {new Date().getFullYear()}
          </div>

          <h2 className="text-[52px] font-[900] leading-[1.05] mb-6">
            Manage.<br />Track.<br />Enroll.
          </h2>

          <p className="text-white/80 text-base leading-relaxed mb-12 max-w-sm">
            Your admissions pipeline, leads, and follow-ups — all in one place. Built for India's offline &amp; hybrid learners.
          </p>

          <div className="flex gap-12">
            <div>
              <div className="text-3xl font-[900]">500+</div>
              <div className="text-[10px] font-[700] uppercase tracking-wider text-white/70 mt-1">Active Leads</div>
            </div>
            <div>
              <div className="text-3xl font-[900]">3</div>
              <div className="text-[10px] font-[700] uppercase tracking-wider text-white/70 mt-1">Tracks</div>
            </div>
            <div>
              <div className="text-3xl font-[900]">98%</div>
              <div className="text-[10px] font-[700] uppercase tracking-wider text-white/70 mt-1">Placement</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
